/**
 * Product entity adapter (P15.3 / #416).
 *
 * Carries the P14.1 `annual_revenue` Decimal field -- the original
 * driver for #416. Drawer hosts two link-table sections:
 * ProductIBSLink + ProductCapabilityLink, both managed via the
 * generic `LinkSection` primitive.
 */
import { i18n } from "shared/i18n";
import { formatSAR, isMoneyNotMeasured } from "shared/money/money";

import { ProductCapabilityLinks, ProductIBSLinks } from "./productLinks";
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export type ProductClassification =
  "critical_business" | "supporting" | "non_critical" | "out_of_scope";

export interface Product {
  id: string;
  code: string;
  name: string;
  description: string | null;
  classification: ProductClassification;
  /** Backend serialises Decimal as string per Pydantic v2. */
  annual_revenue: string | null;
  // P136 (P117f.4): implied-SSOT product-profile fields.
  customer_count: number | null;
  launch_date: string | null;
  owning_business_unit: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductIn {
  code: string;
  name: string;
  description: string | null;
  classification: ProductClassification;
  annual_revenue: string | null;
  customer_count?: number | null;
  launch_date?: string | null;
  owning_business_unit?: string | null;
}

export type ProductPatch = Partial<ProductIn>;

const CLASSIFICATION_OPTIONS = [
  { value: "critical_business", label: "Critical business" },
  { value: "supporting", label: "Supporting" },
  { value: "non_critical", label: "Non-critical" },
  { value: "out_of_scope", label: "Out of scope" },
];

const CLASSIFICATION_TONE: Record<ProductClassification, string> = {
  critical_business: "bg-red-100 text-red-900",
  supporting: "bg-slate-100 text-slate-800",
  non_critical: "bg-amber-100 text-amber-900",
  out_of_scope: "bg-slate-100 text-slate-500",
};

const columns: AdminColumn<Product>[] = [
  {
    key: "code",
    label: "Code",
    sortValue: (r) => r.code,
    render: (r) => <span className="font-mono text-xs text-slate-700">{r.code}</span>,
  },
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
  },
  {
    key: "classification",
    label: "Classification",
    sortValue: (r) => r.classification,
    render: (r) => (
      <span
        data-testid="product-classification-chip"
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
          CLASSIFICATION_TONE[r.classification]
        }`}
      >
        {r.classification.replace(/_/g, " ")}
      </span>
    ),
  },
  {
    key: "annual_revenue",
    label: "Annual revenue",
    align: "right",
    sortValue: (r) =>
      r.annual_revenue === null ? null : Number.parseFloat(r.annual_revenue),
    render: (r) => {
      const formatted = formatSAR(r.annual_revenue);
      if (isMoneyNotMeasured(formatted)) {
        return (
          <span
            data-testid="product-revenue-unmeasured"
            className="text-xs italic text-slate-400"
          >
            {i18n.t("screens.productAdapter.notMeasured")}
          </span>
        );
      }
      return (
        <span
          data-testid="product-revenue-cell"
          className="text-xs tabular-nums text-slate-700"
        >
          {formatted}
        </span>
      );
    },
  },
];

const formFields: FormField<ProductIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
  {
    kind: "select",
    key: "classification",
    label: "Classification",
    required: true,
    options: CLASSIFICATION_OPTIONS,
  },
  {
    kind: "decimal",
    key: "annual_revenue",
    label: "Annual revenue (SAR)",
    currencyHint: "SAR",
  },
  // Implied-SSOT product profile (P136 / P117f.4).
  {
    kind: "number",
    key: "customer_count",
    label: "Customer count",
    min: 0,
    group: "Product profile",
  },
  { kind: "date", key: "launch_date", label: "Launch date", group: "Product profile" },
  {
    kind: "text",
    key: "owning_business_unit",
    label: "Owning business unit",
    maxLength: 255,
    group: "Product profile",
  },
];

export const productAdapter: EntityAdapter<Product, ProductIn, ProductPatch> = {
  i18nPrefix: "modules.foundation.admin.products",
  importKind: "product",
  endpoint: "/v1/admin/foundation/products",
  queryKey: ["foundation-admin", "products"] as const,
  columns,
  defaultSort: { key: "code", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.description ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    classification: "supporting",
    annual_revenue: null,
    customer_count: null,
    launch_date: null,
    owning_business_unit: null,
  }),
  rowToPatch: (r) => ({
    code: r.code,
    name: r.name,
    description: r.description,
    classification: r.classification,
    annual_revenue: r.annual_revenue,
    customer_count: r.customer_count,
    launch_date: r.launch_date,
    owning_business_unit: r.owning_business_unit,
  }),
  extraSections: [ProductIBSLinks, ProductCapabilityLinks],
};
